// @ts-nocheck
/**
 * Tenants Section Component
 * Admin UI for managing tenants with table, pagination, filters
 */

import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { useSelector, useDispatch, useTranslation, useIsRTL, getHttpClient } from '@ufc/core'
import { canManageOrganizationData, selectUserRoleType } from '@ufc/domains/auth'
import {
  Building2,
  Users,
  Clock,
  AlertTriangle,
  Search,
  CheckCircle,
  XCircle,
  ShoppingCart,
  Plus,
  Pencil,
  Eye,
  Ban,
} from '@ufc/uikit'
import {
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Button,
  PaginatedDataTable,
  cn,
  type DataTableColumn,
  type DataTableAction,
} from '@ufc/uikit'

import {
  tenantsActions,
  selectFilteredTenants,
  selectTenantsStats,
  selectTenantsLoading,
  selectTenantsFilter,
  selectSelectedTenant,
  selectTenantsPagination,
  selectCustomerGroups,
  selectCustomerGroupsLoading,
} from '../../store'
import { useTenantsReadable } from '../../copilot/tenants.readable'
import { useTenantsCopilotActions } from '../../copilot/tenants.actions'
import { useTenantsSuggestions } from '../../copilot/tenants.suggestions'
const TenantDetailDrawer = lazy(() => import('../drawers/tenant-detail-drawer').then(m => ({ default: m.TenantDetailDrawer })))
const TenantFormDrawer = lazy(() => import('../drawers/tenant-form-drawer').then(m => ({ default: m.TenantFormDrawer })))
import { formDataToCreateRequest, formDataToUpdateRequest } from '../../domain/utils/tenant-form-schema'
import { getTenantUpdateId } from '../../domain/utils/tenant-identifiers'
import type { Tenant, TenantStatus, TenantChannel } from '../../domain/types/tenant.types'

// ====================================
// Constants
// ====================================
const STATUS_STYLES: Record<TenantStatus, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
}

const STATUS_LABELS: Record<TenantStatus, string> = {
  active: 'admin.active',
  pending: 'admin.pending',
  suspended: 'admin.suspended',
  inactive: 'admin.inactive',
}

const CHANNEL_ICONS: Record<TenantChannel, typeof Building2> = {
  b2b: Building2,
  ecommerce: ShoppingCart,
}

const PAGE_SIZES = [10, 20, 50, 100]

// SAP KDGRP: 11=Transport, 12=Plants, 13=Bag Customers
const BAGS_CUSTOMER_GROUP = '13'
const BULK_CUSTOMER_GROUPS = ['11', '12']

interface DormancyConfig {
  bulkDormancyDays: number
  bagsDormancyDays: number
}

const DEFAULT_DORMANCY_CONFIG: DormancyConfig = {
  bulkDormancyDays: 2,
  bagsDormancyDays: 14,
}

function isDormantTenant(tenant: Tenant, config: DormancyConfig): boolean {
  if (tenant.status !== 'active') return false
  if (!tenant.sapCustomerGroup) return false

  // Use last sales order date if available, otherwise fall back to tenant creation date
  const referenceDate = tenant.lastSalesOrderCreatedAt
    ? new Date(tenant.lastSalesOrderCreatedAt)
    : new Date(tenant.createdAt)

  const now = new Date()
  const daysSinceReference = (now.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24)

  if (tenant.sapCustomerGroup === BAGS_CUSTOMER_GROUP) {
    return daysSinceReference > config.bagsDormancyDays
  }
  if (BULK_CUSTOMER_GROUPS.includes(tenant.sapCustomerGroup)) {
    return daysSinceReference > config.bulkDormancyDays
  }
  return false
}

async function fetchDormancyConfig(): Promise<DormancyConfig> {
  try {
    const http = getHttpClient()
    const res = await http.get<unknown>('/api/portal-config')
    const json = res.data as Record<string, unknown>
    const data = (json?.data ?? json) as Record<string, unknown>
    return {
      bulkDormancyDays: (data?.bulkDormancyDays as number) ?? DEFAULT_DORMANCY_CONFIG.bulkDormancyDays,
      bagsDormancyDays: (data?.bagsDormancyDays as number) ?? DEFAULT_DORMANCY_CONFIG.bagsDormancyDays,
    }
  } catch {
    return DEFAULT_DORMANCY_CONFIG
  }
}

interface TenantsSectionProps {
  onViewUsers?: (tenantId: string) => void
}

// ====================================
// External Functions
// ====================================
type TranslateFn = ReturnType<typeof useTranslation>['t']

function getStatusFilterOptions(t: TranslateFn) {
  return [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'pending', label: t('admin.pending', 'Pending') },
    { value: 'active', label: t('admin.active', 'Active') },
    { value: 'suspended', label: t('admin.suspended', 'Suspended') },
    { value: 'inactive', label: t('admin.inactive', 'Inactive') },
  ]
}

function getChannelFilterOptions(t: TranslateFn) {
  return [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'b2b', label: t('admin.b2b', 'B2B') },
    { value: 'ecommerce', label: t('admin.ecommerce', 'E-commerce') },
  ]
}

function getChannelLabel(channel: TenantChannel, t: TranslateFn): string {
  return channel === 'b2b' ? t('admin.b2b', 'B2B') : t('admin.ecommerce', 'E-commerce')
}

// ====================================
// Stats Card Component
// ====================================
interface StatsCardProps {
  title: string
  value: number
  icon: React.ReactNode
  variant?: 'default' | 'warning' | 'success' | 'danger'
  onClick?: () => void
  isActive?: boolean
  'data-testid'?: string
}

function StatsCard({ title, value, icon, variant = 'default', onClick, isActive, 'data-testid': testId }: StatsCardProps) {
  const variantStyles = {
    default: 'bg-card hover:bg-accent/50',
    warning: 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30',
    success: 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30',
    danger: 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30',
  }

  const gradientColors = {
    default: 'from-blue-500 to-blue-600',
    warning: 'from-amber-500 to-amber-600',
    success: 'from-green-500 to-green-600',
    danger: 'from-red-500 to-red-600',
  }

  return (
    <Card
      className={cn(
        'transition-[box-shadow,border-color] duration-200 cursor-pointer border border-border/50',
        variantStyles[variant],
        isActive && 'ring-2 ring-primary'
      )}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <div className="rounded-lg bg-primary/10 p-3">{icon as any}</div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p
            className={cn(
              'text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent',
              gradientColors[variant]
            )}
          >
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

// ====================================
// Main Section Component
// ====================================
export function TenantsSection({ onViewUsers }: TenantsSectionProps) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const isRTL = useIsRTL()

  // Redux selectors
  const tenants = useSelector(selectFilteredTenants)
  const stats = useSelector(selectTenantsStats)
  const isLoading = useSelector(selectTenantsLoading)
  const filter = useSelector(selectTenantsFilter)
  const pagination = useSelector(selectTenantsPagination)
  const selectedTenant = useSelector(selectSelectedTenant)
  const customerGroups = useSelector(selectCustomerGroups)
  const isCustomerGroupsLoading = useSelector(selectCustomerGroupsLoading)
  const roleType = useSelector(selectUserRoleType)
  const canManageOrganization = canManageOrganizationData(roleType)

  // Local state
  const [searchValue, setSearchValue] = useState(filter.search ?? '')
  const [activeStatFilter, setActiveStatFilter] = useState<TenantStatus | 'all'>('all')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [formDrawerOpen, setFormDrawerOpen] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [dormancyConfig, setDormancyConfig] = useState<DormancyConfig>(DEFAULT_DORMANCY_CONFIG)

  // Fetch tenants on mount
  useEffect(() => {
    dispatch(tenantsActions.fetchTenants(undefined))
    dispatch(tenantsActions.fetchCustomerGroups())
  }, [dispatch])

  useEffect(() => {
    fetchDormancyConfig().then(setDormancyConfig)
  }, [])

  useEffect(() => {
    setSearchValue(filter.search ?? '')
  }, [filter.search])

  useEffect(() => {
    setActiveStatFilter((filter.status as TenantStatus | 'all') ?? 'all')
  }, [filter.status])

  // Register Copilot integrations
  useTenantsReadable()
  useTenantsCopilotActions()
  useTenantsSuggestions()

  // Table columns
  const columns: DataTableColumn<Tenant>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('admin.tenants.columns.name', 'Tenant'),
        width: 'w-[220px]',
        render: (tenant) => (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-foreground truncate">{tenant.name}</p>
              <p className="text-xs text-muted-foreground font-mono">{tenant.code}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'channel',
        header: t('admin.tenants.columns.channel', 'Channel'),
        width: 'w-[120px]',
        render: (tenant) => {
          const ChannelIcon = tenant.channel ? CHANNEL_ICONS[tenant.channel] : null
          return (
            <div className="flex items-center gap-2">
              {ChannelIcon && <ChannelIcon className="h-4 w-4 text-muted-foreground" />}
              <span className="text-sm">{tenant.channel ? getChannelLabel(tenant.channel, t) : '-'}</span>
            </div>
          )
        },
      },
      {
        key: 'customerGroup',
        header: t('admin.tenants.columns.customerGroup', 'Customer Group'),
        width: 'w-[150px]',
        render: (tenant) =>
          tenant.customerGroup ? (
            <Badge variant="outline" className="text-xs">
              {tenant.customerGroup.name}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          ),
      },
      {
        key: 'sapBusinessPartnerNumber',
        header: t('admin.tenants.columns.sapBusinessPartnerNumber', 'SAP BP #'),
        width: 'w-[160px]',
        render: (tenant) =>
          tenant.sapBusinessPartnerNumber ? (
            <span className="text-sm font-mono text-muted-foreground">
              {tenant.sapBusinessPartnerNumber}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          ),
      },
      {
        key: 'users',
        header: t('admin.tenants.columns.users', 'Users'),
        width: 'w-[80px]',
        render: (tenant) => (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span>{tenant.usersCount ?? 0}</span>
          </div>
        ),
      },
      {
        key: 'status',
        header: t('admin.tenants.columns.status', 'Status'),
        width: 'w-[140px]',
        render: (tenant) => {
          const dormant = isDormantTenant(tenant, dormancyConfig)
          if (dormant) {
            return (
              <Badge className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                {t('admin.tenants.dormant', 'Dormant')}
              </Badge>
            )
          }
          return (
            <Badge className={cn('text-xs', STATUS_STYLES[tenant.status])}>
              {t(STATUS_LABELS[tenant.status], tenant.status)}
            </Badge>
          )
        },
      },
      {
        key: 'lastSalesOrderCreatedAt',
        header: t('admin.tenants.columns.lastOrder', 'Last Order'),
        width: 'w-[130px]',
        render: (tenant) =>
          tenant.lastSalesOrderCreatedAt ? (
            <span className="text-sm text-muted-foreground">
              {new Date(tenant.lastSalesOrderCreatedAt).toLocaleDateString(isRTL ? 'ar-SA' : 'en-SA', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          ),
      },
      {
        key: 'createdAt',
        header: t('admin.tenants.columns.created', 'Created'),
        width: 'w-[120px]',
        render: (tenant) => (
          <span className="text-sm text-muted-foreground">
            {new Date(tenant.createdAt).toLocaleDateString(isRTL ? 'ar-SA' : 'en-SA', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        ),
      },
    ],
    [t, isRTL, dormancyConfig]
  )

  // Table actions
  const actions: DataTableAction<Tenant>[] = useMemo(
    () => {
      const baseActions: DataTableAction<Tenant>[] = [
        {
          label: t('admin.viewDetails', 'View Details'),
          icon: Eye,
          onClick: (tenant) => handleSelectTenant(tenant),
        },
      ]

      if (!canManageOrganization) {
        return baseActions
      }

      return [
        ...baseActions,
        {
          label: t('admin.edit', 'Edit'),
          icon: Pencil,
          onClick: (tenant) => handleOpenEditForm(tenant),
        },
        {
          label: t('admin.approve', 'Approve'),
          icon: CheckCircle,
          onClick: (tenant) => handleApproveTenant(tenant),
          hidden: (tenant) => tenant.status !== 'pending',
        },
        {
          label: t('admin.suspend', 'Suspend'),
          icon: Ban,
          onClick: (tenant) => handleSuspendTenant(tenant),
          hidden: (tenant) => tenant.status === 'suspended' || tenant.status === 'inactive',
          destructive: true,
          separator: true,
        },
        {
          label: t('admin.activate', 'Activate'),
          icon: CheckCircle,
          onClick: (tenant) => handleActivateTenant(tenant),
          hidden: (tenant) => tenant.status !== 'suspended' && tenant.status !== 'inactive',
        },
      ]
    },
    [t, canManageOrganization]
  )

  // Handlers
  const handleStatusChange = useCallback(
    (status: string) => {
      setActiveStatFilter(status as TenantStatus | 'all')
      dispatch(tenantsActions.filterByStatus(status as TenantStatus | 'all'))
    },
    [dispatch]
  )

  const handleChannelChange = useCallback(
    (channel: string) => {
      dispatch(tenantsActions.filterByChannel(channel as TenantChannel | 'all'))
    },
    [dispatch]
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }, [])

  const handleSearchSubmit = useCallback(() => {
    dispatch(tenantsActions.searchTenants(searchValue))
  }, [dispatch, searchValue])

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearchSubmit()
      }
    },
    [handleSearchSubmit]
  )

  const handleSelectTenant = useCallback(
    (tenant: Tenant) => {
      dispatch(tenantsActions.selectTenant(tenant))
      setDrawerOpen(true)
    },
    [dispatch]
  )

  const handleDrawerClose = useCallback(
    (open: boolean) => {
      setDrawerOpen(open)
      if (!open) {
        dispatch(tenantsActions.selectTenant(null))
      }
    },
    [dispatch]
  )

  const handleChannelUpdate = useCallback(
    (tenantId: string, channel: TenantChannel) => {
      dispatch(tenantsActions.updateTenant({ tenantId, data: { channel } }))
    },
    [dispatch]
  )

  const handleReactivateTenant = useCallback(
    (tenantId: string) => {
      dispatch(tenantsActions.updateTenant({ tenantId, data: { status: 'active' } as Partial<Tenant> }))
    },
    [dispatch]
  )

  const getCustomerGroupDocumentId = useCallback(
    (tenant: Tenant) => {
      if (!tenant.customerGroup) return undefined
      const match = customerGroups.find((g) => g.id === tenant.customerGroup?.id)
      return match?.documentId
    },
    [customerGroups]
  )

  const handleApproveTenantById = useCallback(
    (tenantId: string, customerGroupDocumentId?: string, sapCustomerGroup?: string) => {
      dispatch(tenantsActions.approveTenant({ tenantId, customerGroupDocumentId, sapCustomerGroup }))
    },
    [dispatch]
  )

  const handleApproveTenant = useCallback(
    (tenant: Tenant) => {
      handleApproveTenantById(
        getTenantUpdateId(tenant),
        getCustomerGroupDocumentId(tenant),
        tenant.sapCustomerGroup
      )
    },
    [handleApproveTenantById, getCustomerGroupDocumentId]
  )

  const handleSuspendTenant = useCallback(
    (tenant: Tenant) => {
      dispatch(tenantsActions.suspendTenant({ tenantId: getTenantUpdateId(tenant) }))
    },
    [dispatch]
  )

  const handleActivateTenant = useCallback(
    (tenant: Tenant) => {
      dispatch(tenantsActions.updateTenant({ tenantId: getTenantUpdateId(tenant), data: { status: 'active' } as Partial<Tenant> }))
    },
    [dispatch]
  )

  const handleStatCardClick = useCallback(
    (status: TenantStatus | 'all') => {
      handleStatusChange(status)
    },
    [handleStatusChange]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(tenantsActions.setPage(page))
    },
    [dispatch]
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      dispatch(tenantsActions.setPageSize(pageSize))
    },
    [dispatch]
  )

  // Form drawer handlers
  const handleOpenCreateForm = useCallback(() => {
    setEditingTenant(null)
    setFormDrawerOpen(true)
  }, [])

  const handleOpenEditForm = useCallback((tenant: Tenant) => {
    setEditingTenant(tenant)
    setFormDrawerOpen(true)
  }, [])

  const handleFormDrawerClose = useCallback((open: boolean) => {
    setFormDrawerOpen(open)
    if (!open) {
      setEditingTenant(null)
    }
  }, [])

  const handleFormSubmit = useCallback(
    (data: ReturnType<typeof formDataToCreateRequest> | ReturnType<typeof formDataToUpdateRequest>, isEdit: boolean) => {
      if (isEdit && editingTenant) {
        dispatch(tenantsActions.updateTenant({
          tenantId: getTenantUpdateId(editingTenant),
          data: data as Partial<Tenant>,
          customerGroupId: data.customerGroupId,
        }))
      } else {
        // Add default status for new tenants
        const createData = data as ReturnType<typeof formDataToCreateRequest>
        dispatch(tenantsActions.createTenant({
          ...createData,
          status: 'pending',
        }))
      }
      setFormDrawerOpen(false)
      setEditingTenant(null)
    },
    [dispatch, editingTenant]
  )

  const statusOptions = getStatusFilterOptions(t)
  const channelOptions = getChannelFilterOptions(t)

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className={cn('flex items-center justify-between')}>
        <div className={cn(isRTL && 'text-right')}>
          <h1 className="text-2xl font-bold text-foreground">
            {t('admin.tenantManagement', 'Tenant Management')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('admin.tenantManagementDesc', 'Manage organizations and their access')}
          </p>
        </div>
        {canManageOrganization && (
          <Button onClick={handleOpenCreateForm} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('admin.createTenant', 'Create Tenant')}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatsCard
          title={t('admin.totalTenants', 'Total Tenants')}
          value={stats.total}
          icon={<Building2 className="h-5 w-5 text-primary" />}
          variant="default"
          onClick={() => handleStatCardClick('all')}
          isActive={activeStatFilter === 'all'}
        />
        <StatsCard
          title={t('admin.pendingApproval', 'Pending Approval')}
          value={stats.pending}
          icon={<Clock className="h-5 w-5 text-amber-600" />}
          variant="warning"
          onClick={() => handleStatCardClick('pending')}
          isActive={activeStatFilter === 'pending'}
          data-testid="tenants-pending-card"
        />
        <StatsCard
          title={t('admin.activeTenants', 'Active Tenants')}
          value={stats.active}
          icon={<CheckCircle className="h-5 w-5 text-green-600" />}
          variant="success"
          onClick={() => handleStatCardClick('active')}
          isActive={activeStatFilter === 'active'}
        />
        <StatsCard
          title={t('admin.suspendedTenants', 'Suspended')}
          value={stats.suspended}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          variant="danger"
          onClick={() => handleStatCardClick('suspended')}
          isActive={activeStatFilter === 'suspended'}
        />
      </div>

      {/* Filters */}
      <Card className="border border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="tenants-search"
                placeholder={t('admin.searchTenants', 'Search tenants...')}
                value={searchValue}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchSubmit}
                className="ps-9 bg-background/50 border-border/50"
              />
            </div>

            <Select value={filter.status ?? 'all'} onValueChange={handleStatusChange}>
              <SelectTrigger data-testid="tenants-filter-status" className="w-full sm:w-[150px] bg-background/50 border-border/50">
                <SelectValue placeholder={t('admin.status', 'Status')} />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filter.channel ?? 'all'} onValueChange={handleChannelChange}>
              <SelectTrigger className="w-full sm:w-[150px] bg-background/50 border-border/50">
                <SelectValue placeholder={t('admin.channel', 'Channel')} />
              </SelectTrigger>
              <SelectContent>
                {channelOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table with Pagination */}
      <PaginatedDataTable
        data-testid="tenants-table"
        data={tenants}
        columns={columns}
        actions={actions}
        getRowKey={(tenant) => tenant.id}
        onRowClick={handleSelectTenant}
        selectedRowKey={selectedTenant?.id}
        isRTL={isRTL}
        emptyState={{
          icon: Building2,
          title: t('admin.noTenantsFound', 'No tenants found'),
          description: t('admin.noTenantsFoundDesc', 'Try adjusting your search or filter criteria'),
        }}
        page={pagination.page}
        pageSize={pagination.pageSize}
        totalItems={pagination.total}
        totalPages={pagination.pageCount}
        pageSizes={PAGE_SIZES}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showPageSizeSelector
        showItemCount
      />

      {/* Tenant Detail Drawer */}
      <Suspense fallback={null}>
      <TenantDetailDrawer
        tenant={selectedTenant}
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        isLoading={isLoading}
        customerGroups={customerGroups}
        isCustomerGroupsLoading={isCustomerGroupsLoading}
        onApprove={
          canManageOrganization
            ? (tenantId, customerGroupDocumentId, sapCustomerGroup) => {
                handleApproveTenantById(tenantId, customerGroupDocumentId, sapCustomerGroup)
              }
            : undefined
        }
        onSuspend={
          canManageOrganization
            ? (tenantId) => dispatch(tenantsActions.suspendTenant({ tenantId }))
            : undefined
        }
        onReactivate={canManageOrganization ? handleReactivateTenant : undefined}
        onChannelChange={canManageOrganization ? handleChannelUpdate : undefined}
        onViewUsers={canManageOrganization ? onViewUsers : undefined}
      />
      </Suspense>

      {/* Tenant Form Drawer (Create/Edit) */}
      {canManageOrganization && (
        <Suspense fallback={null}>
        <TenantFormDrawer
          open={formDrawerOpen}
          onOpenChange={handleFormDrawerClose}
          tenant={editingTenant}
          onSubmit={handleFormSubmit}
          isLoading={isLoading}
          isRTL={isRTL}
        />
        </Suspense>
      )}

      {/* Approve is handled inline in TenantDetailDrawer */}
    </div>
  )
}
